import { mapboxgl } from 'core/utils/map/mapbox-gl';
import { MAP_INITIAL_POSITION } from '@features/regional-overview/regional-overview.models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderTrapPin } from '../network-management.utils';
import { useNetworkManagementStore } from '../network-management.zustand';

export const useNetworkManagementMap = () => {
  const { isModalOpen, coordinates } = useNetworkManagementStore();
  const [mapbox, setMapbox] = useState<mapboxgl.Map>();
  const [mapboxIsLoaded, setMapboxIsLoaded] = useState<boolean>(false);
  const markerRef = useRef<mapboxgl.Marker>();

  const mapCenter = useMemo(() => {
    if (coordinates && coordinates.length >= 2) {
      return [coordinates[0], coordinates[1]] as [number, number];
    }
    return MAP_INITIAL_POSITION;
  }, [coordinates]);

  const handleMapbox = useCallback(
    (mapboxProp: mapboxgl.Map) => {
      setMapbox(mapboxProp);
      setMapboxIsLoaded(true);
    },
    [setMapbox, setMapboxIsLoaded]
  );

  useEffect(() => {
    if (!mapbox || !mapboxIsLoaded || !coordinates || coordinates.length < 2) {
      return;
    }

    if (markerRef.current) {
      markerRef.current.remove();
    }

    const [longitude, latitude] = coordinates;

    const markerElement = renderTrapPin();

    const marker = new mapboxgl.Marker({ element: markerElement }).setLngLat([longitude, latitude]).addTo(mapbox);

    markerRef.current = marker;

    mapbox.flyTo({
      center: [longitude, latitude],
      zoom: 15,
      duration: 1000
    });

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
      }
    };
  }, [mapbox, mapboxIsLoaded, coordinates]);

  useEffect(() => {
    if (!isModalOpen && markerRef.current) {
      markerRef.current.remove();
      markerRef.current = undefined;
    }
  }, [isModalOpen]);

  return {
    mapbox,
    mapboxIsLoaded,
    mapCenter,
    handleMapbox
  };
};
